import React from 'react';
import { Card } from '../ui/Card';
import { ArrowUpRight, ArrowRight, Clock, Users, CheckCircle2, DollarSign } from 'lucide-react';
interface KPIStripProps {
  metrics: {
    totalLeads: number;
    totalConverted: number;
    totalFunded: number;
  } | null;
  onDrillDown: (metric: string) => void;
}
export function KPIStrip({
  metrics,
  onDrillDown
}: KPIStripProps) {
  if (!metrics) return null;
  const conversionRate = metrics.totalLeads > 0 ? (metrics.totalConverted / metrics.totalLeads * 100).toFixed(1) : '0.0';
  const fundedRate = metrics.totalLeads > 0 ? (metrics.totalFunded / metrics.totalLeads * 100).toFixed(1) : '0.0';
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-113">
      {/* Leads Created */}
      <Card className="group cursor-pointer hover:shadow-md hover:border-[#0176D3] transition-all duration-200 p-0 overflow-hidden" onClick={() => onDrillDown('Created')} data-id="element-114">
        <div className="flex items-stretch" data-id="element-115">
          <div className="w-1.5 bg-[#0176D3] shrink-0" data-id="element-116" />
          <div className="flex-1 p-5" data-id="element-117">
            <div className="flex items-start justify-between mb-3" data-id="element-118">
              <span className="text-sm font-medium text-[#706E6B]" data-id="element-119">
                Leads Created
              </span>
              <div className="w-8 h-8 rounded-lg bg-[#0176D3]/10 flex items-center justify-center" data-id="element-120">
                <Users className="h-4 w-4 text-[#0176D3]" data-id="element-121" />
              </div>
            </div>
            <div className="text-3xl font-bold text-[#181818] mb-2" data-id="element-122">
              {metrics.totalLeads.toLocaleString()}
            </div>
            <div className="text-xs text-[#706E6B] flex items-center" data-id="element-123">
              <span className="inline-flex items-center bg-[#0176D3]/10 text-[#0176D3] px-2 py-0.5 rounded font-medium" data-id="element-124">
                Cohort
              </span>
            </div>
          </div>
        </div>
      </Card>

      {/* Converted */}
      <Card className="group cursor-pointer hover:shadow-md hover:border-[#0176D3] transition-all duration-200 p-0 overflow-hidden" onClick={() => onDrillDown('Converted')} data-id="element-125">
        <div className="flex items-stretch" data-id="element-126">
          <div className="w-1.5 bg-[#0D9DDA] shrink-0" data-id="element-127" />
          <div className="flex-1 p-5" data-id="element-128">
            <div className="flex items-start justify-between mb-3" data-id="element-129">
              <span className="text-sm font-medium text-[#706E6B]" data-id="element-130">
                Converted to Opp
              </span>
              <div className="w-8 h-8 rounded-lg bg-[#0D9DDA]/10 flex items-center justify-center" data-id="element-131">
                <CheckCircle2 className="h-4 w-4 text-[#0D9DDA]" data-id="element-132" />
              </div>
            </div>
            <div className="text-3xl font-bold text-[#181818] mb-2" data-id="element-133">
              {metrics.totalConverted.toLocaleString()}
            </div>
            <div className="text-xs text-[#0D9DDA] flex items-center font-medium" data-id="element-134">
              <ArrowRight className="h-3 w-3 mr-1" data-id="element-135" />
              {conversionRate}% of created
            </div>
          </div>
        </div>
      </Card>

      {/* Funded */}
      <Card className="group cursor-pointer hover:shadow-md hover:border-[#04844B] transition-all duration-200 p-0 overflow-hidden" onClick={() => onDrillDown('Funded')} data-id="element-136">
        <div className="flex items-stretch" data-id="element-137">
          <div className="w-1.5 bg-[#04844B] shrink-0" data-id="element-138" />
          <div className="flex-1 p-5" data-id="element-139">
            <div className="flex items-start justify-between mb-3" data-id="element-140">
              <span className="text-sm font-medium text-[#706E6B]" data-id="element-141">
                Funded Loans
              </span>
              <div className="w-8 h-8 rounded-lg bg-[#04844B]/10 flex items-center justify-center" data-id="element-142">
                <DollarSign className="h-4 w-4 text-[#04844B]" data-id="element-143" />
              </div>
            </div>
            <div className="text-3xl font-bold text-[#04844B] mb-2" data-id="element-144">
              {metrics.totalFunded.toLocaleString()}
            </div>
            <div className="text-xs text-[#04844B] flex items-center font-medium" data-id="element-145">
              <ArrowUpRight className="h-3 w-3 mr-1" data-id="element-146" />
              {fundedRate}% pull-through
            </div>
          </div>
        </div>
      </Card>

      {/* Cycle Time - Not clickable */}
      <Card className="p-0 overflow-hidden" data-id="element-147">
        <div className="flex items-stretch" data-id="element-148">
          <div className="w-1.5 bg-[#706E6B] shrink-0" data-id="element-149" />
          <div className="flex-1 p-5" data-id="element-150">
            <div className="flex items-start justify-between mb-3" data-id="element-151">
              <span className="text-sm font-medium text-[#706E6B]" data-id="element-152">
                Avg. Cycle Time
              </span>
              <div className="w-8 h-8 rounded-lg bg-[#706E6B]/10 flex items-center justify-center" data-id="element-153">
                <Clock className="h-4 w-4 text-[#706E6B]" data-id="element-154" />
              </div>
            </div>
            <div className="text-3xl font-bold text-[#181818] mb-2" data-id="element-155">
              24
              <span className="text-lg font-normal text-[#706E6B] ml-1" data-id="element-156">
                days
              </span>
            </div>
            <div className="text-xs text-[#706E6B] flex items-center" data-id="element-157">
              Created → Funded
            </div>
          </div>
        </div>
      </Card>
    </div>;
}